import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  APIClient, 
  APIClientConfig, 
  ProjectsResponse, 
  ComponentsResponse, 
  ProjectComponentsResponse,
  APIProject,
  HealthResponse,
  APISchema,
  APIError
} from '../types/api';
import { schemaValidator } from '../utils/schemaValidation';
import { getApiConfig } from '../config/api';

export class PatternLabAPIClient implements APIClient {
  private client: AxiosInstance;
  private config: APIClientConfig;
  private schemaLoaded = false;

  constructor(config?: Partial<APIClientConfig>) {
    const defaultConfig = getApiConfig();
    this.config = {
      baseURL: config?.baseURL || defaultConfig.BASE_URL,
      apiKey: config?.apiKey || defaultConfig.API_KEY,
      timeout: config?.timeout || defaultConfig.TIMEOUT,
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'x-api-key': this.config.apiKey,
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for logging
    this.client.interceptors.request.use(
      (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('API Response Error:', error);
        if (error.response?.data) {
          console.error('API Error Details:', error.response.data);
        }
        return Promise.reject(error);
      }
    );
  }

  private async ensureSchemaLoaded(): Promise<void> {
    if (!this.schemaLoaded) {
      try {
        await schemaValidator.loadSchema(`${this.config.baseURL}/schema`);
        this.schemaLoaded = true;
        console.log('API Schema loaded successfully');
      } catch (error) {
        console.warn('Failed to load API schema, continuing without validation:', error);
        // Continue without schema validation
      }
    }
  }

  private async validateResponse<T>(
    response: AxiosResponse<T>,
    validator: (data: any) => data is T
  ): Promise<T> {
    try {
      await this.ensureSchemaLoaded();
      
      if (!validator(response.data)) {
        console.error('Response validation failed. Actual response data:', JSON.stringify(response.data, null, 2));
        throw new Error('Response validation failed');
      }
      
      return response.data;
    } catch (error) {
      console.error('Response validation error:', error);
      // Return the data anyway, but log the validation error
      return response.data;
    }
  }

  async getProjects(): Promise<ProjectsResponse> {
    try {
      const response = await this.client.get<ProjectsResponse>('/api/projects');
      return await this.validateResponse(response, schemaValidator.validateProjectsArray.bind(schemaValidator));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw this.handleError(error);
    }
  }

  async getProject(projectId: string): Promise<APIProject> {
    try {
      const response = await this.client.get<APIProject>(`/api/projects/${projectId}`);
      return await this.validateResponse(response, schemaValidator.validateProject.bind(schemaValidator));
    } catch (error) {
      console.error(`Failed to fetch project ${projectId}:`, error);
      throw this.handleError(error);
    }
  }

  async getAllComponents(): Promise<ComponentsResponse> {
    try {
      const response = await this.client.get<ComponentsResponse>('/api/components');
      return await this.validateResponse(response, schemaValidator.validateComponentsArray.bind(schemaValidator));
    } catch (error) {
      console.error('Failed to fetch all components:', error);
      throw this.handleError(error);
    }
  }

  async getProjectComponents(projectId: string): Promise<ProjectComponentsResponse> {
    try {
      const response = await this.client.get<ProjectComponentsResponse>(`/api/components/${projectId}`);
      return await this.validateResponse(response, schemaValidator.validateComponentsArray.bind(schemaValidator));
    } catch (error) {
      console.error(`Failed to fetch components for project ${projectId}:`, error);
      throw this.handleError(error);
    }
  }

  async getHealth(): Promise<HealthResponse> {
    try {
      const response = await this.client.get<HealthResponse>('/health');
      return await this.validateResponse(response, schemaValidator.validateHealthResponse.bind(schemaValidator));
    } catch (error) {
      console.error('Failed to fetch health status:', error);
      throw this.handleError(error);
    }
  }

  async getSchema(): Promise<APISchema> {
    try {
      const response = await this.client.get<APISchema>('/schema');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch schema:', error);
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (error.response?.data) {
      const apiError = error.response.data as APIError;
      return new Error(`API Error: ${apiError.message || error.message}`);
    }
    return new Error(error.message || 'Unknown API error');
  }

  // Utility methods
  getConfig(): APIClientConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<APIClientConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Update axios instance
    this.client.defaults.baseURL = this.config.baseURL;
    this.client.defaults.timeout = this.config.timeout;
    this.client.defaults.headers['x-api-key'] = this.config.apiKey;
  }
}

// Create and export a singleton instance
export const apiClient = new PatternLabAPIClient();
